import React, { useState, useEffect } from "react";
import {
  Button,
  Checkbox,
  Column,
  Columns,
  Divider,
  Heading,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  TextField,
  TextFieldBase,
} from "@origin-digital/ods-core";
import {
  Field,
  FieldInputProps,
  FieldMetaProps,
  FieldProps,
  FormikProps,
  useFormikContext,
} from "formik";
import FirstLastName from "./FormComponents/FirstLastName";
import PhoneNumber from "./FormComponents/PhoneNumber";
import EmailCommunications from "./FormComponents/EmailCommunications";
import BillingAddress from "./FormComponents/BillingAddress";
import { styled } from "@mui/material";
import { AccountPayer, DocumentTypeEnum, FormValues } from "./types";
import { apiClient } from "src/api/apiService";
import { accountFormFieldMapper } from "src/api/apiHelper";
import FormContext from "src/context/FormContext";
import {
  accountBlockCompanyEmptyFields,
  formikFieldsBatchUpdater,
  secondaryAccountEmptyFields,
  showUpdatePremiseAlert,
} from "./helper";
import FormFieldLayout from "./FormComponents/FormFieldLayout";
import UpdatePremiseAlert from "./UpdatePremiseAlert";
import HomeUserAccountBlockContent from "./HomeUser/HomeUserAccountBlockContent";
import PermissionsControlledWrapper from "../Permissions/PermissionsControlledWrapper";
import { PermissionEnum } from "../Permissions/types";
import OnlineFormAccountHolderDetails from "./OnlineFormAccountHolderDetails";
import { DateOfBirthFormikField } from "../Fields/DateOfBirth";
import { NewPremiseFormFileUploadFormikField } from "./NewPremise/NewPremiseFormFileUploadFormikField";

const StyledRadioGroup = styled(RadioGroup)({
  display: "flex",
  justifyContent: "flexStart",
});

interface IAccountBlock {
  premiseId: string | null;
  allowSave?: boolean;
  homeUser: boolean;
}

const AccountBlock = (props: IAccountBlock) => {
  const { premiseId, allowSave = true, homeUser } = props;
  const { values, setFieldValue, setErrors } = useFormikContext<FormValues>();
  const [savingBlock, setSavingBlock] = useState(false);
  const { setDisplayingSnackbar, setDisplayingAlertBanner, formState } =
    React.useContext(FormContext);
  const accountPayers = [
    values.electricityAccountPayer,
    values.gasLine?.accountPayer,
    values.gasMeter?.accountPayer,
    values.both?.accountPayer,
    values.internetAccountPayer,
  ];
  const showOptionForUploadPdf = React.useMemo(() => {
    if (
      [
        values.electricityAccountPayer,
        values.gasMeter?.accountPayer,
        values.both?.accountPayer,
      ].includes(AccountPayer.SOMEONE_ELSE)
    ) {
      return true;
    } else if (
      [values.gasLine?.accountPayer, values.internetAccountPayer].includes(
        AccountPayer.SOMEONE_ELSE
      )
    ) {
      return false;
    }
    return true;
  }, [
    values.electricityAccountPayer,
    values.gasMeter?.accountPayer,
    values.both?.accountPayer,
    values.gasLine?.accountPayer,
    values.internetAccountPayer,
  ]);

  //cleaning up account block ui pathways
  useEffect(() => {
    if (values.accountHolder === "person") {
      formikFieldsBatchUpdater(setFieldValue, accountBlockCompanyEmptyFields);
    }
  }, [values.accountHolder]);
  useEffect(() => {
    if (values.secondaryAccountHolder === false) {
      formikFieldsBatchUpdater(setFieldValue, secondaryAccountEmptyFields);
    }
  }, [values.secondaryAccountHolder]);
  //end cleanup
  const hyperlinkText = (
    <>
      <Text>
        Request for a new energy account form is to be signed by the account
        holder. Please check that all the fields match what's on the form to
        avoid unnecessary delays.
      </Text>
    </>
  );
  const saveAccountBlock = async (v: FormValues) => {
    setSavingBlock(true);
    try {
      await apiClient.post(
        `/draft/account/${premiseId}`,
        accountFormFieldMapper(v)
      );
      setSavingBlock(false);
      setDisplayingSnackbar({
        message: "Account saved",
        tone: "positive",
      });
      setDisplayingAlertBanner(null);
    } catch (e) {
      console.error("Failed to save account:", e);
      setDisplayingSnackbar({
        message: "Failed to save account",
        tone: "caution",
      });
      setSavingBlock(false);
    }
  };

  return (
    <>
      {showUpdatePremiseAlert(
        [
          values.electricityAccountPayer,
          values.gasLine.accountPayer,
          values.gasMeter.accountPayer,
          values.both.accountPayer,
        ],
        !!values.accountId,
        !!premiseId
      ) ? (
        <Stack space="xlarge">
          <UpdatePremiseAlert />
        </Stack>
      ) : (
        <Stack
          space={{
            xs: "xlarge",
            md: "xxlarge",
          }}
        >
          {!homeUser && showOptionForUploadPdf && (
            <Field
              name="accountHolderDetailsType"
              id="account-holder-details-type"
            >
              {({ field }: { field: FormikProps<FieldProps> }) => (
                <StyledRadioGroup
                  {...field}
                  id="account-holder-details-type"
                  name="accountHolderDetailsType"
                  label="How would you like to submit account holder details?"
                  value={values.accountHolderDetailsType}
                >
                  <Radio id="online" label="Online form" value="online" />
                  <Radio id="pdf" label="Upload PDF form" value="pdf" />
                </StyledRadioGroup>
              )}
            </Field>
          )}
          {values.accountHolderDetailsType === "online" && (
            <OnlineFormAccountHolderDetails
              values={values}
              setErrors={setErrors}
              savingBlock={savingBlock}
              setSavingBlock={setSavingBlock}
              saveAccountBlock={saveAccountBlock}
              homeUser={homeUser}
              showSubTitle={showOptionForUploadPdf}
              saveButtonText={
                formState.electricityService?.channel === "CLAIM"
                  ? "Lodge claim"
                  : "Save Account"
              }
            />
          )}
          {values.accountHolderDetailsType === "pdf" && (
            <Stack
              space={{
                xs: "xlarge",
                md: "xxlarge",
              }}
            >
              <Field name="accountHolder" id="account-holder">
                {({ field }: { field: FormikProps<FieldProps> }) => (
                  <StyledRadioGroup
                    {...field}
                    id="account-holder"
                    name="accountHolder"
                    label="Account holder"
                    value={values.accountHolder}
                  >
                    <Radio id="person" label="Personal" value="person" />
                    <Radio id="company" label="Business" value="company" />
                  </StyledRadioGroup>
                )}
              </Field>
              {values.accountHolder === "company" ? (
                <Field name="companyName" id="company-name">
                  {({
                    field,
                    meta,
                  }: {
                    field: FormikProps<FieldProps>;
                    meta: FieldMetaProps<FormValues>;
                  }) => (
                    <TextField
                      {...field}
                      name="companyName"
                      id="company-name"
                      variant="outlined"
                      label="Company name"
                      hideHelperTextSpace={true}
                      error={!!meta.error}
                      helperText={meta.error}
                    />
                  )}
                </Field>
              ) : null}
              {values.accountHolder === "company" ? (
                <Columns
                  collapseBelow="md"
                  space={{
                    xs: "none",
                    md: "medium",
                  }}
                >
                  <Column>
                    <Field name="abn" id="abn">
                      {({
                        field,
                        meta,
                      }: {
                        field: FormikProps<FieldProps>;
                        meta: FieldMetaProps<FormValues>;
                      }) => (
                        <TextField
                          {...field}
                          id="abn"
                          name="abn"
                          variant="outlined"
                          label="ABN"
                          hideHelperTextSpace={true}
                          error={!!meta.error}
                          helperText={meta.error}
                        />
                      )}
                    </Field>
                  </Column>
                  <Column />
                </Columns>
              ) : null}
              {values.accountHolder === "company" ? <Divider /> : null}
              {values.accountHolder === "company" ? (
                <Heading variant="h4">Primary contact</Heading>
              ) : null}
              {/* First + last name */}
              <FirstLastName homeUser={homeUser} />
              <Columns collapseBelow="md">
                <Column width="2/3">
                  <Columns collapseBelow="md">
                    <Column>
                      <DateOfBirthFormikField
                        id={"account-primary-dob"}
                        name={"accountPrimaryDob"}
                        label={"Date of birth"}
                      />
                    </Column>
                    <Column>
                      <Field
                        name="accountPrimaryPhone"
                        id="account-phone-number"
                        component={PhoneNumber}
                      />
                    </Column>
                  </Columns>
                </Column>
                <Column width="1/3" />
              </Columns>
              <EmailCommunications
                id="account-primary-email"
                name="accountPrimaryEmail"
                homeUser={homeUser}
              />
              <BillingAddress />
              {values.accountHolder === "person" ? <Divider /> : null}
              {values.accountHolder === "person" ? (
                <Columns
                  collapseBelow="md"
                  space={{
                    xs: "none",
                    md: "xlarge",
                  }}
                  alignY="stretch"
                >
                  {/* Fields */}
                  <Column width="2/3">
                    <Stack
                      space={{
                        xs: "xlarge",
                        md: "xxlarge",
                      }}
                    >
                      <Switch
                        id="secondary-account-holder"
                        label="Secondary account holder"
                        //@ts-expect-error: need item callback
                        onChange={(e, item) => {
                          setFieldValue("secondaryAccountHolder", item);
                        }}
                        checked={values.secondaryAccountHolder}
                      />
                      {values.secondaryAccountHolder ? (
                        <Stack>
                          <Columns
                            collapseBelow="md"
                            space={{
                              xs: "xlarge",
                              md: "medium",
                            }}
                          >
                            <Column>
                              <Field
                                name="accountSecondaryFirstName"
                                id="account-secondary-first-name"
                              >
                                {({
                                  field,
                                  meta,
                                }: {
                                  field: FormikProps<FieldProps>;
                                  meta: FieldMetaProps<FormValues>;
                                }) => (
                                  <TextField
                                    {...field}
                                    id="account-secondary-first-name"
                                    variant="outlined"
                                    label="First name"
                                    hideHelperTextSpace={true}
                                    error={!!meta.error}
                                    helperText={meta.error}
                                  />
                                )}
                              </Field>
                            </Column>
                            <Column>
                              <Field
                                name="accountSecondaryLastName"
                                id="account-secondary-last-name"
                              >
                                {({
                                  field,
                                  meta,
                                }: {
                                  field: FormikProps<FieldProps>;
                                  meta: FieldMetaProps<FormValues>;
                                }) => (
                                  <TextField
                                    {...field}
                                    id="account-secondary-last-name"
                                    variant="outlined"
                                    label="Last name"
                                    hideHelperTextSpace={true}
                                    error={!!meta.error}
                                    helperText={meta.error}
                                  />
                                )}
                              </Field>
                            </Column>
                          </Columns>
                          <Columns
                            collapseBelow="md"
                            space={{
                              xs: "xlarge",
                              md: "medium",
                            }}
                          >
                            <Column>
                              <DateOfBirthFormikField
                                id={"account-secondary-dob"}
                                name={"accountSecondaryDob"}
                                label={"Date of birth"}
                              />
                            </Column>
                            <Column>
                              <Field
                                name="accountSecondaryPhone"
                                id="account-secondary-phone"
                              >
                                {({
                                  field,
                                  meta,
                                }: {
                                  field: FormikProps<FieldProps>;
                                  meta: FieldMetaProps<FormValues>;
                                }) => (
                                  <TextFieldBase
                                    {...field}
                                    id="account-phone"
                                    variant="outlined"
                                    label="Phone"
                                    error={!!meta.error}
                                    helperText={meta.error}
                                    // hideHelperTextSpace={true}
                                  />
                                )}
                              </Field>
                            </Column>
                          </Columns>
                          <Field
                            name="accountSecondaryEmail"
                            id="account-secondary-email"
                          >
                            {({
                              field,
                              meta,
                            }: {
                              field: FormikProps<FieldProps>;
                              meta: FieldMetaProps<FormValues>;
                            }) => (
                              <TextField
                                {...field}
                                id="account-email"
                                variant="outlined"
                                label="Email"
                                error={!!meta.error}
                                hideHelperTextSpace={true}
                                helperText={meta.error}
                              />
                            )}
                          </Field>

                          <Field type="checkbox" name="addSecondaryNameToBill">
                            {({
                              field,
                            }: {
                              field: FieldInputProps<boolean>;
                            }) => {
                              return (
                                <Checkbox
                                  label="Add secondary account holder's name to Origin bills"
                                  id="addSecondaryNameToBill"
                                  checked={field.checked}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "addSecondaryNameToBill",
                                      e.target.checked
                                    )
                                  }
                                />
                              );
                            }}
                          </Field>
                        </Stack>
                      ) : null}
                      {/* End Email */}
                    </Stack>
                  </Column>
                  {/* End Fields */}
                  {/* HelperText */}
                  <Column width="1/3">
                    <Stack>
                      {
                        <Text variant="body-small" tone="neutralLight">
                          The secondary account holder will be able to access
                          all of the primary account holder's Origin accounts.
                        </Text>
                      }
                    </Stack>
                  </Column>
                  {/* End HelperText */}
                </Columns>
              ) : null}
              {(!homeUser ||
                accountPayers.includes(AccountPayer.SOMEONE_ELSE)) && (
                <Divider />
              )}
              {(!homeUser ||
                accountPayers.includes(AccountPayer.SOMEONE_ELSE)) && (
                <FormFieldLayout text={hyperlinkText} multipleFields>
                  <Text>Customer consent</Text>
                  <Field name="customerConsentFiles">
                    {({ field }: FieldProps) => (
                      <NewPremiseFormFileUploadFormikField
                        accountId={
                          values.accountId ? values.accountId : undefined
                        }
                        documentTypes={[
                          DocumentTypeEnum.NEW_ENERGY_ACCOUNT_FORM,
                        ]}
                        field={field}
                      />
                    )}
                  </Field>
                </FormFieldLayout>
              )}
              {homeUser &&
                !accountPayers.includes(AccountPayer.SOMEONE_ELSE) && (
                  <Divider />
                )}
              {homeUser &&
                !accountPayers.includes(AccountPayer.SOMEONE_ELSE) && (
                  <HomeUserAccountBlockContent />
                )}
              <PermissionsControlledWrapper
                requiredPermission={PermissionEnum.CREATE_REQUEST}
              >
                {allowSave ? (
                  savingBlock ? (
                    <Button spinner>Action</Button>
                  ) : (
                    <Button
                      onClick={() => {
                        saveAccountBlock(values);
                      }}
                    >
                      Save Account
                    </Button>
                  )
                ) : null}
              </PermissionsControlledWrapper>
            </Stack>
          )}
        </Stack>
      )}

      {/* </Accordion> */}

      {/* End Premise section */}
    </>
  );
};

export default AccountBlock;
